/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useIntl} from 'react-intl'
import {validatePassword, usePasswordValidationMessages} from '../../utils/password-utils'
import {useXSSProtection} from './../../hooks/use-xss-protection'
import {useToast} from '../../hooks/use-toast'

/**
 * Custom hook that provides configuration for password update form fields.
 *
 * This hook is designed to be used with a form management library, such as React Hook Form,
 * to handle the validation and management of password fields in a form. It returns an object
 * containing configuration for the current password and new password fields, including their
 * names, labels, default values, validation rules, and error handling.
 *
 * @param {Object} params - The parameters for configuring the password fields.
 * @param {Object} params.form - The form object containing control and form state.
 * @param {string} [params.prefix=''] - An optional prefix to prepend to the field names.
 *
 * @returns {Object} An object containing configuration for the password fields:
 * - `currentPassword`: Configuration for the current password field.
 * - `password`: Configuration for the new password field.
 */
export default function useUpdatePasswordFields({
    form: {
        control,
        formState: {errors}
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()
    const validationMessages = usePasswordValidationMessages()
    const {validateField} = useXSSProtection()
    const toast = useToast()

    const fields = {
        currentPassword: {
            name: `${prefix}currentPassword`,
            label: formatMessage({
                defaultMessage: 'Current Password',
                id: 'use_update_password_fields.label.current_password'
            }),
            defaultValue: '',
            type: 'password',
            autoComplete: 'current-password',
            rules: {
                required: validationMessages.required.current_password,
                validate: (value) => validateField(toast, value, 'updatepassword-form', 'password')

            },
            error: errors[`${prefix}currentPassword`],
            control
        },
        password: {
            name: `${prefix}password`,
            label: formatMessage({
                defaultMessage: 'New Password',
                id: 'use_update_password_fields.label.new_password'
            }),
            type: 'password',
            autoComplete: 'new-password',
            defaultValue: '',
            rules: {
                required: validationMessages.required.new_password,
                validate: {
                    hasMinChars: (val) =>
                        validatePassword(val).hasMinChars || validationMessages.minChars,
                    hasUppercase: (val) =>
                        validatePassword(val).hasUppercase || validationMessages.uppercase,
                    hasLowercase: (val) =>
                        validatePassword(val).hasLowercase || validationMessages.lowercase,
                    hasNumber: (val) =>
                        validatePassword(val).hasNumber || validationMessages.number,
                    hasSpecialChar: (val) =>
                        validatePassword(val).hasSpecialChar || validationMessages.specialChar
                }
            },
            error: errors[`${prefix}password`],
            control
        }
    }

    return fields
}
